// 🔧 entry_builder.js
// ✅ Purpose: Entry builder for the LGV Demo Engine
// ✅ Features:
// - Creates industry-specific demo entries in Firestore
// - Uses the event created by Event Builder
// - Uses categories created by Category Builder
// - Stores entries under /events/{eventId}/cars to match current LGV entry model
// - Uses centralized demo size configuration
// - Routes entry generation through the generic Experience Factory industry dispatcher
// - Preserves current Car Show behavior while supporting future industry generators
// - Returns created entry metadata for downstream User/Vote Builder migration
// - Fully block-commented for clarity and future devs

import { collection, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { logger } from "../../utils/logger";
import { getDemoSizeConfig } from "../config/demo_sizes";
import { generateEntriesForIndustry } from "../generators/entry_generator";
import { commitBatchedWrites } from "../repositories/batched_writes";

export const buildEntries = async ({
  eventResult,
  categoryResult,
  demoConfig,
  industryTemplate,
} = {}) => {
  const eventId = eventResult?.eventId;
  const sizeConfig = getDemoSizeConfig(demoConfig?.size);
  const createdCategories = categoryResult?.createdCategories || [];
  const categoryNames = createdCategories.filter((category) => !category?.isDefault).map((category) =>
    typeof category === "string"
      ? category
      : category?.name || "General"
  );

  if (!eventId) {
    throw new Error("Entry Builder requires an eventId.");
  }

  if (!createdCategories.length) {
    throw new Error("Entry Builder requires created categories.");
  }

  const plannedEntries = generateEntriesForIndustry({
    industryKey:
      industryTemplate?.key ||
      demoConfig?.industry ||
      "car_show",
    count:
      Number.isInteger(industryTemplate?.fixedEntryCount) &&
      industryTemplate.fixedEntryCount > 0
        ? industryTemplate.fixedEntryCount
        : sizeConfig.entryCount,
    categories: categoryNames,
  });

  logger.log("🧱 Entry Builder creating Firestore entries:", {
    value: {
      eventId,
      demoSize: sizeConfig.key,
      plannedEntryCount: plannedEntries.length,
      categoryCount: createdCategories.length,
      sampleEntry: plannedEntries[0] || null,
    },
  });

  const createdEntries = [];

  const operations = [];
  // The purpose-built challenge keeps its thirteen manifest-defined team/category assignments.
  const defaultCategory = (industryTemplate?.key || demoConfig?.industry) === "sap_ai_agents_challenge"
    ? null
    : createdCategories.find((category) => category?.isDefault);

  for (let index = 0; index < plannedEntries.length; index += 1) {
    const plannedEntry = plannedEntries[index];

    // Keep industry examples intact while exercising one uncategorized entry.
    const matchedCategory = defaultCategory && index === plannedEntries.length - 1
      ? defaultCategory
      : createdCategories.find(
        (category) => category.name === plannedEntry.categoryName
      ) || createdCategories[index % createdCategories.length];

    const entryRef = doc(collection(db, "events", eventId, "cars"));

    const entryDoc = {
      cid: entryRef.id,
      title: plannedEntry.title,
      name: plannedEntry.title,
      ownerName: plannedEntry.ownerName,
      description: plannedEntry.description,
      entryType:
        plannedEntry.entryType || industryTemplate?.entryLabel || "Entry",
      participantType:
        plannedEntry.participantType ||
        industryTemplate?.experience?.participantType ||
        industryTemplate?.entryLabel ||
        "Participant",
      subtitle: plannedEntry.subtitle || "",
      organizationName: plannedEntry.organizationName || "",
      details: plannedEntry.details || {},
      focusArea:
        plannedEntry.focusArea || "",
      categoryId:
        matchedCategory?.id || "",
      catid: matchedCategory?.id || "",

      categoryName:
        matchedCategory?.isDefault ? "" : matchedCategory?.name ||
        plannedEntry.categoryName ||
        "General",
      demoSeed: true,
      demoIndustry: industryTemplate?.key || demoConfig?.industry || "car_show",
      displayOrder: plannedEntry.displayOrder || index + 1,
      approved: true,
      status: "Approved",
      hidden: false,
      imgUrl:
        plannedEntry.imgUrl || "",
      photoUrl:
        plannedEntry.imgUrl || "",
      imageUrl:
        plannedEntry.imageUrl || "",
      images:
        plannedEntry.images || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      regcnt:
        plannedEntry.regcnt ?? index + 1,
      registrationLabel:
        plannedEntry.registrationLabel ||
        `Test Entry #${String(index + 1).padStart(3, "0")}`,
      year:
        plannedEntry.year || "",
      make:
        plannedEntry.make || "",
      model:
        plannedEntry.model || "",
      color:
        plannedEntry.color || "",
      ownerCity:
        plannedEntry.ownerCity || "",
      socialLinks:
        plannedEntry.socialLinks || [],      
    };

    operations.push((batch) => batch.set(entryRef, entryDoc));

    createdEntries.push({
      id: entryRef.id,
      ref: entryRef,
      title: entryDoc.title,
      ownerName: entryDoc.ownerName,
      categoryId: entryDoc.categoryId,
      categoryName: entryDoc.categoryName,
      displayOrder: entryDoc.displayOrder,
      imageUrl: entryDoc.imageUrl || entryDoc.imgUrl || entryDoc.photoUrl || "",
    });
  }

  await commitBatchedWrites({ operations });

  logger.log("✅ Entry Builder created Firestore entries:", {
    value: {
      eventId,
      createdEntryCount: createdEntries.length,
    },
  });

  return {
    builder: "entry_builder",
    ready: true,
    createdCount: createdEntries.length,
    createdEntries,
    message: "Entry Builder created demo entries.",
  };
};

export const buildEntryShell = async ({
  eventResult,
  categoryResult,
  demoConfig,
  industryTemplate,
} = {}) => {
  if (!eventResult?.eventId || !categoryResult?.createdCategories?.length) {
    const sizeConfig = getDemoSizeConfig(demoConfig?.size);
    const plannedCategories = industryTemplate?.defaultCategories || [];

    const plannedEntries = generateEntriesForIndustry({
      industryKey:
        industryTemplate?.key ||
        demoConfig?.industry ||
        "car_show",
      count:
      Number.isInteger(industryTemplate?.fixedEntryCount) &&
      industryTemplate.fixedEntryCount > 0
        ? industryTemplate.fixedEntryCount
        : sizeConfig.entryCount,
      categories: plannedCategories,
    });

    logger.warn("⚠️ Entry Builder shell used before Event/Category results.", {
      value: {
        plannedEntryCount: plannedEntries.length,
      },
    });

    return {
      builder: "entry_builder",
      ready: false,
      plannedCount: plannedEntries.length,
      plannedEntries,
      entryLabel: industryTemplate?.entryLabel || "Entry",
      message: "Entry Builder needs event and category results before Firestore creation.",
    };
  }

  return buildEntries({
    eventResult,
    categoryResult,
    demoConfig,
    industryTemplate,
  });
};
